import {
  ApiChartAttribute,
  ApiChartLayout,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiOperator,
  ApiYAxisChartType,
} from "@incendium/api";
import { Box } from "@mui/material";
import CarouselCard from "Components/UI/CarouselCard";
import { StandardAnalyticsChartStatCard } from "features/analytics";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_DASHBOARD_CWV_BOUNCE_RATE,
  CHART_KEY_DASHBOARD_CWV_EXIT_RATE,
  CHART_KEY_DASHBOARD_CWV_OVERALL,
  CHART_KEY_DASHBOARD_CWV_PERFORMANCE_BAR,
  CHART_KEY_DASHBOARD_CWV_TABLE,
} from "features/analytics/constants/chartKeys";
import { table5Rows } from "features/analytics/constants/constants";
import { useIsMobile } from "Hooks/useIsMobile";

interface ICoreWebVitalsTabProps {
  attributes: ApiChartAttribute[];
}

const CWV_DIMENSIONS = [
  ApiDimension.DIMENSION_LOCATION,
  ApiDimension.DIMENSION_PATH,
];

function CoreWebVitalsTab({ attributes }: ICoreWebVitalsTabProps) {
  const isMobile = useIsMobile();

  return (
    <>
      <CarouselCard
        title="Key Stats"
        emptyMessage="No key stats available"
        items={[
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_CWV_OVERALL}
            staticAttributes={attributes}
            chart={{
              name: "Average CWV Score",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE],
                },
              ],
              displayOptions: {
                statTitle: "Average CWV Score",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_CWV_BOUNCE_RATE}
            staticAttributes={attributes}
            chart={{
              name: "Bounce Rate",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_BOUNCE_RATE],
                },
              ],
              displayOptions: {
                statTitle: "Bounce Rate",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_CWV_EXIT_RATE}
            staticAttributes={attributes}
            chart={{
              name: "Exit Rate",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_EXIT_RATE],
                },
              ],
              displayOptions: {
                statTitle: "Exit Rate",
              },
              comparison: true,
            }}
          />,
        ]}
      />

      <Box
        sx={{
          height: { xs: 320, md: 400 },
          flexShrink: 0,
          width: "100%",
          minWidth: 0,
        }}
      >
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_CWV_PERFORMANCE_BAR}
          staticAttributes={attributes}
          ignoreAttrModel
          chart={{
            name: "Lowest Performing Pages by",
            dimension: [ApiDimension.DIMENSION_LOCATION],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.GRAPH,
            attributes: [],
            limit: 10,
            orderBy: `${ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE} ASC`,
            having: [
              {
                metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                operator: ApiOperator.IS_GREATER_THAN,
                value: 0,
              },
              {
                metric: ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                operator: ApiOperator.IS_GREATER_THAN,
                value: 0,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE],
                chart: {
                  [ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE]:
                    ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noAttributionToggle: true,
              layout: ApiChartLayout.VERTICAL,
              noLegend: true,
              availableDimensions: CWV_DIMENSIONS,
              fullAttributesList: [ApiDimension.DIMENSION_NOT_SET],
            },
          }}
        />
      </Box>

      <Box
        sx={{
          height: table5Rows,
          flexShrink: 0,
          width: "100%",
          minWidth: 0,
          overflowX: "auto",
        }}
      >
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_CWV_TABLE}
          staticAttributes={attributes}
          ignoreAttrModel
          chart={{
            name: "Core Web Vitals by",
            dimension: [ApiDimension.DIMENSION_LOCATION],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            orderBy: `${ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE} ASC`,
            having: [
              {
                metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                operator: ApiOperator.IS_GREATER_THAN,
                value: 0,
              },
              {
                metric: ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                operator: ApiOperator.IS_GREATER_THAN,
                value: 0,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_PAGEVIEWS_COUNT,
                  ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                  ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                  ApiMetric.METRIC_BOUNCE_RATE,
                  ApiMetric.METRIC_EXIT_RATE,
                  ApiMetric.METRIC_REVENUE,
                ],
              },
            ],
            displayOptions: {
              noAttributionToggle: true,
              rowsPerPage: 0,
              showTotals: true,
              availableDimensions: CWV_DIMENSIONS,
              fullAttributesList: [ApiDimension.DIMENSION_NOT_SET],
              tableColWidths: {
                [ApiDimension.DIMENSION_LOCATION]: isMobile ? 200 : 350,
                [ApiDimension.DIMENSION_PATH]: isMobile ? 160 : 280,
              },
            },
          }}
        />
      </Box>
    </>
  );
}

export default CoreWebVitalsTab;
